
import {
  applyOptionsToTag,
  getOption,
  UNICODE_TYPE_REGEX,
  applyUnicodeExtensionToTag,
  addLikelySubtags,
  removeLikelySubtags,
  parseUnicodeLanguageId,
  getCalendarPreferenceDataForRegion,
  createArrayFromListOrRestricted,
  supportedValuesOf,
  getHourCyclesPreferenceDataForLocaleOrRegion,
  getTimeZonePreferenceForRegion,
  translateCharacterOrder,
  getWeekDataForRegion,
  hashObject,
  getCanonicalLocales,
  resolveLocale,
  getCanonicalTimeZoneIdentifier
} from './utils.js'
import numberingSystems from './data/numberingSystems.js'
import characterOrders from './data/characterOrders.js'
import ObjectCache from './ObjectCache.js'
import supportedLocalesData from './data/supportedLocales.js'
import NumberingSystem from './NumberingSystem.js'
import SlotMap from '@neumatter/slot-map'
import { timeZoneCodes } from './data/timeZones.js'
import DateTimeFormat from './DateTimeFormat.js'
import DurationFormat from './DurationFormat.js'

export const monthsLong = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

export const monthsShort = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

export const monthsNarrow = [
  'J',
  'F',
  'M',
  'A',
  'M',
  'J',
  'J',
  'A',
  'S',
  'O',
  'N',
  'D'
]

export const weekdaysLong = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]

export const weekdaysShort = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export const weekdaysNarrow = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

function getWeekdays (length) {
  switch (length) {
    case 'narrow':
      return [...weekdaysNarrow]
    case 'short':
      return [...weekdaysShort]
    case 'long':
      return [...weekdaysLong]
    case 'numeric':
      return ['1', '2', '3', '4', '5', '6', '7']
    default:
      return null
  }
}

function mapMonths (f) {
  const ms = []
  for (let i = 1; i <= 12; i++) {
    const dt = new Date(2016, i, 1)
    ms.push(f(dt))
  }
  return ms
}

function mapWeekdays (f) {
  const ms = []
  for (let i = 1; i <= 7; i++) {
    const dt = new Date(2016, 11, 13 + i)
    ms.push(f(dt))
  }
  return ms
}

function getMonths (length) {
  switch (length) {
    case 'narrow':
      return [...monthsNarrow]
    case 'short':
      return [...monthsShort]
    case 'long':
      return [...monthsLong]
    case 'numeric':
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']
    case '2-digit':
      return [
        '01',
        '02',
        '03',
        '04',
        '05',
        '06',
        '07',
        '08',
        '09',
        '10',
        '11',
        '12'
      ]
    default:
      return null
  }
}

const tExtensions = {
  i0: 'inputMethod',
  k0: 'keyboard',
  t0: 'machine',
  x0: 'privateUse'
}

const defaultResolved = (() => {
  const r = new Intl.DateTimeFormat().resolvedOptions()
  let res = r.locale + '-u-ca-' + r.calendar
  if ('numberingSystem' in r) {
    res += '-nu-' + r.numberingSystem
  }
  if ('timeZone' in r) {
    const tz = r.timeZone

    for (const tzl in timeZoneCodes) {
      if (timeZoneCodes[tzl] === tz) {
        res += '-tz-' + tzl
        break
      }
    }
  }

  return res
})()

/**
 *
 * @param {Locale} obj
 * @returns {Locale}
 */
function getInternalMaxLocale (obj) {
  let locale = SlotMap.getInternal(obj, 'internalMaxLocale')

  if (locale === undefined) {
    locale = obj.maximize()
    SlotMap.setInternal(obj, 'internalMaxLocale', locale)
  }

  return locale
}

const SYMBOL_IS_LOCALE = Symbol.for('neumatter.intl.Locale.isLocale')

export default class Locale {
  static getCanonicalLocales (locales) {
    return getCanonicalLocales(locales)
  }

  static isLocale (input) {
    return (
      typeof input === 'object' &&
      typeof input[SYMBOL_IS_LOCALE] === 'boolean'
    )
  }

  static resolve (locales, options) {
    options = options || {}
    const requestedLocales = Locale.getCanonicalLocales(locales)
    const opts = Object.create(null)

    const matcher = getOption(
      opts,
      'localeMatcher',
      'string',
      ['best fit', 'lookup'],
      'best fit'
    )

    opts.localeMatcher = matcher

    const localeData = supportedLocalesData.reduce((res, locale) => {
      res[locale] = {}
      return res
    }, {})

    return new Locale(resolveLocale(
      new Set(supportedLocalesData),
      requestedLocales,
      opts,
      Locale.relevantExtensionKeys,
      localeData,
      () => Locale.systemLocale
    ).locale)
  }

  /**
   *
   * @param {string | Locale} tag
   * @param {Intl.LocaleOptions} options
   */
  static from (tag, options) {
    return new Locale(tag, options).maximize()
  }

  static relevantExtensionKeys = ['ca', 'co', 'hc', 'kf', 'kn', 'nu']

  static get polyfilled () {
    return true
  }

  static systemLocale = (() => new Locale(defaultResolved))()

  #internal = {}

  /**
   *
   * @param {string | Locale} tag
   * @param {Intl.LocaleOptions} opts
   */
  constructor (tag, opts) {
    let relevantExtensionKeys = SlotMap.getInternal(Locale, 'relevantExtensionKeys')
    const slotMap = SlotMap.get(this)

    if (relevantExtensionKeys === undefined) {
      relevantExtensionKeys = ['ca', 'co', 'hc', 'kf', 'kn', 'nu', 'tz']
      SlotMap.setInternal(Locale, 'relevantExtensionKeys', relevantExtensionKeys)
    }

    switch (typeof tag) {
      case 'undefined':
        throw new TypeError(
          "First argument to Locale constructor can't be empty or missing"
        )
      case 'string':
        break
      case 'object':
        tag = String(tag)
        break
      default:
        throw new TypeError('tag must be a string or object')
    }

    opts = opts || {}

    tag = applyOptionsToTag(tag, opts)
    const options = Object.create(null)

    const calendar = getOption(
      opts,
      'calendar',
      'string',
      undefined,
      undefined
    )

    if (calendar !== undefined) {
      if (!UNICODE_TYPE_REGEX.test(calendar)) {
        throw new RangeError('invalid calendar')
      }
    }

    options.ca = calendar

    const timeZone = getOption(
      opts,
      'timeZone',
      'string',
      undefined,
      undefined
    )

    if (timeZone !== undefined) {
      if (!UNICODE_TYPE_REGEX.test(calendar)) {
        throw new RangeError('invalid timeZone')
      }
    }

    options.tz = timeZone

    const collation = getOption(
      opts,
      'collation',
      'string',
      undefined,
      undefined
    )

    if (collation !== undefined) {
      if (!UNICODE_TYPE_REGEX.test(collation)) {
        throw new RangeError('invalid collation')
      }
    }

    options.co = collation

    const hc = getOption(
      opts,
      'hourCycle',
      'string',
      ['h11', 'h12', 'h23', 'h24'],
      undefined
    )

    options.hc = hc

    const kf = getOption(
      opts,
      'caseFirst',
      'string',
      ['upper', 'lower', 'false'],
      undefined
    )

    options.kf = kf

    let kn = getOption(
      opts,
      'numeric',
      'boolean',
      undefined,
      undefined
    )

    if (kn !== undefined) {
      kn = String(kn)
    }

    options.kn = kn

    const numberingSystem = getOption(
      opts,
      'numberingSystem',
      'string',
      undefined,
      undefined
    )

    if (numberingSystem !== undefined) {
      if (!UNICODE_TYPE_REGEX.test(numberingSystem)) {
        throw new RangeError('Invalid numberingSystem')
      }
    }

    options.nu = numberingSystem
    const r = applyUnicodeExtensionToTag(tag, options, relevantExtensionKeys)
    slotMap.set('baseName', r.baseName)
    slotMap.set('calendar', r.ca)
    slotMap.set('collation', r.co)
    slotMap.set('hourCycle', r.hc)
    slotMap.set('timeZone', r.tz)

    if (relevantExtensionKeys.indexOf('kf') > -1) {
      slotMap.set('caseFirst', r.kf)
    }
    if (relevantExtensionKeys.indexOf('kn') > -1) {
      slotMap.set('numeric', r.kn)
    }

    slotMap.set('numberingSystemID', r.nu)
  }

  get [SYMBOL_IS_LOCALE] () {
    return true
  }

  get [Symbol.toStringTag] () {
    return 'Locale'
  }

  get baseName () {
    return SlotMap.getInternal(this, 'baseName')
    // return emitUnicodeLanguageId(parseUnicodeLanguageId(locale))
  }

  get calendar () {
    return SlotMap.getInternal(this, 'calendar')
  }

  get collation () {
    return SlotMap.getInternal(this, 'collation')
  }

  get hourCycle () {
    return SlotMap.getInternal(this, 'hourCycle')
  }

  get caseFirst () {
    return SlotMap.getInternal(this, 'caseFirst')
  }

  get numeric () {
    return SlotMap.getInternal(this, 'numeric')
  }

  /**
   *
   * @type {NumberingSystem}
   */
  get numberingSystem () {
    const slotMap = SlotMap.get(this)
    let numberingSystem = slotMap.get('numberingSystem')

    if (numberingSystem === undefined) {
      let numberingSystemID = slotMap.get('numberingSystemID')

      if (numberingSystemID === undefined) {
        numberingSystemID = this.numberingSystems[0]
      }

      numberingSystem = new NumberingSystem(this.baseName, { id: numberingSystemID })
      slotMap.set('numberingSystem', numberingSystem)
    }

    return numberingSystem
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.language
   */
  get language () {
    const locale = this.baseName
    return parseUnicodeLanguageId(locale).lang
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.script
   */
  get script () {
    const locale = this.baseName
    return parseUnicodeLanguageId(locale).script
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.region
   */
  get region () {
    const locale = this.baseName
    return parseUnicodeLanguageId(locale).region
  }

  get timeZone () {
    const tz = SlotMap.getInternal(this, 'timeZone')

    if (tz !== undefined) {
      const tzn = timeZoneCodes[tz]

      if (tzn === undefined) {
        return tzn
      }

      return getCanonicalTimeZoneIdentifier(tzn)
    }

    return tz
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.calendars
   */
  get calendars () {
    const slotMap = SlotMap.get(this)

    const restricted = slotMap.get('calendar')
    const locale = slotMap.get('baseName')

    let region
    if (locale !== 'root') {
      region = getInternalMaxLocale(this).region
    }

    const preferredCalendars = getCalendarPreferenceDataForRegion(region)
    return createArrayFromListOrRestricted(preferredCalendars, restricted)
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.collations
   */
  get collations () {
    const slotMap = SlotMap.get(this)

    const restricted = slotMap.get('collation')
    const locale = slotMap.get('baseName')

    const supportedCollations = supportedValuesOf('collation', locale).filter(
      co => co !== 'standard' && co !== 'search'
    )
    supportedCollations.sort()

    return createArrayFromListOrRestricted(supportedCollations, restricted)
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.hourCycles
   */
  get hourCycles () {
    const slotMap = SlotMap.get(this)

    const restricted = slotMap.get('hourCycle')
    const locale = slotMap.get('baseName')

    let region
    if (locale !== 'root') {
      region = getInternalMaxLocale(this).region
    }

    const preferredHourCycles = getHourCyclesPreferenceDataForLocaleOrRegion(
      locale,
      region
    )
    return createArrayFromListOrRestricted(preferredHourCycles, restricted)
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.numberingSystems
   */
  get numberingSystems () {
    const slotMap = SlotMap.get(this)

    const restricted = slotMap.get('numberingSystem')
    const locale = slotMap.get('baseName')
    const language = this.language

    const localeNumberingSystems =
      numberingSystems[locale] ?? numberingSystems[language]

    if (localeNumberingSystems) {
      return createArrayFromListOrRestricted(
        [...localeNumberingSystems],
        restricted
      )
    }

    return createArrayFromListOrRestricted([], restricted)
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.timeZones
   */
  get timeZones () {
    const slotMap = SlotMap.get(this)

    const locale = slotMap.get('baseName')
    const region = parseUnicodeLanguageId(locale).region

    if (!region) {
      return undefined
    }

    const preferredTimeZones = getTimeZonePreferenceForRegion(region)
    preferredTimeZones.sort()

    return Array.from(preferredTimeZones)
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.textInfo
   */
  get textInfo () {
    try {
      const info = Object.create(Object.prototype)
      const slotMap = SlotMap.get(this)

      const locale = slotMap.get('baseName')
      const dir = translateCharacterOrder(characterOrders[locale])

      Object.defineProperty(info, 'direction', {
        value: dir,
        writable: true,
        enumerable: true,
        configurable: true
      })

      return info
    } catch (e) {
      throw new TypeError('Error retrieving textInfo')
    }
  }

  get units () {
    const slotMap = SlotMap.get(this)

    const restricted = slotMap.get('unit')
    const locale = slotMap.get('baseName')

    const supportedUnits = supportedValuesOf('unit', locale).filter(
      co => co !== 'standard' && co !== 'search'
    )
    supportedUnits.sort()

    return createArrayFromListOrRestricted(supportedUnits, restricted)
  }

  /**
   * https://tc39.es/proposal-intl-locale/#sec-Intl.Locale.prototype.weekInfo
   */
  get weekInfo () {
    try {
      const info = {}
      const slotMap = SlotMap.get(this)
      const locale = slotMap.get('baseName')
      let region
      if (locale !== 'root') {
        region = getInternalMaxLocale(this).region
      }

      const wi = getWeekDataForRegion(region)
      const we = wi.weekend

      Object.defineProperty(info, 'firstDay', {
        value: wi.firstDay,
        writable: true,
        enumerable: true,
        configurable: true
      })

      Object.defineProperty(info, 'weekend', {
        value: we,
        writable: true,
        enumerable: true,
        configurable: true
      })

      Object.defineProperty(info, 'minimalDays', {
        value: wi.minimalDays,
        writable: true,
        enumerable: true,
        configurable: true
      })

      return info
    } catch (e) {
      throw new TypeError('Error retrieving weekInfo')
    }
  }

  /**
   *
   * @param {Intl.DateTimeFormatOptions} options
   * @returns {DateTimeFormat}
   */
  createDateTimeFormat (options) {
    const intlDateTimeOptions = Object.create(
      options === undefined ? {} : options
    )

    if (!('timeZone' in intlDateTimeOptions)) {
      const timeZone = this.timeZone
      if (timeZone !== undefined) {
        intlDateTimeOptions.timeZone = timeZone
      }
    }

    const cache = ObjectCache.get(this)
    const keyHash = hashObject(intlDateTimeOptions)
    let df = cache.get(keyHash)

    if (df === undefined) {
      df = new DateTimeFormat(this.toString(), intlDateTimeOptions)
      cache.set(keyHash, df)
    }

    return df
  }

  createDurationFormat (options) {
    const intlDurationOptions = Object.create(
      options === undefined ? {} : options
    )

    const cache = ObjectCache.get(this)
    const keyHash = hashObject(intlDurationOptions)
    let df = cache.get(keyHash)

    if (df === undefined) {
      df = new DurationFormat(this.toString(), intlDurationOptions)
      cache.set(keyHash, df)
    }

    return df
  }

  /**
   * https://www.unicode.org/reports/tr35/#Likely_Subtags
   */
  maximize () {
    const locale = this.baseName
    try {
      const maximizedLocale = addLikelySubtags(locale)

      /*
      if (maximizedLocale.indexOf('-nu-') === -1 && maximizedLocale.indexOf('-ca-') === -1) {
        if (maximizedLocale.indexOf('-u-') === -1) {
          maximizedLocale += '-u'
        }

        const options = new Intl.DateTimeFormat(locale).resolvedOptions()
        maximizedLocale += '-nu-' + options.numberingSystem
        maximizedLocale += '-ca-' + options.calendar
      }
      */

      return new Locale(maximizedLocale)
    } catch (e) {
      return new Locale(locale)
    }
  }

  /**
   * https://www.unicode.org/reports/tr35/#Likely_Subtags
   */
  minimize () {
    const locale = this.baseName
    try {
      const minimizedLocale = removeLikelySubtags(locale)
      return new Locale(minimizedLocale)
    } catch (e) {
      return new Locale(locale)
    }
  }

  inspect () {
    return `Locale(${this.toString()})`
  }

  [Symbol.for('nodejs.util.inspect.custom')] () {
    return `Locale(\x1b[35m${this.toString()}\x1b[0m)`
  }

  [Symbol.toPrimitive] () {
    return this.toString()
  }

  toString () {
    const response = this.baseName
    let ext = ''
    let hasExt = false
    const numberingSystemID = SlotMap.getInternal(this, 'numberingSystemID')

    if (numberingSystemID !== undefined) {
      ext += `-nu-${numberingSystemID}`
      hasExt = true
    }

    if (this.calendar !== undefined) {
      ext += `-ca-${this.calendar}`
      hasExt = true
    }

    const timeZone = SlotMap.getInternal(this, 'timeZone')
    if (timeZone !== undefined) {
      ext += `-tz-${timeZone}`
      hasExt = true
    }

    return hasExt ? response + '-u' + ext : response
  }

  /**
   *
   * @param {'long' | 'short' | 'narrow' | 'numeric'} style
   * @param {boolean} format
   * @return {Array<string>}
   */
  listMonths (style, format = false) {
    const mode = this.listingMode
    if (mode === 'error') {
      return null
    } else if (mode === 'en') {
      return getMonths(style)
    } else {
      const cache = ObjectCache.get(this)
      const intl = format
        ? { month: style, day: 'numeric' }
        : { month: style }

      const formatStr = format ? 'monthsCacheFormat' : 'monthsCacheStandalone'
      let months = cache.get(formatStr)

      if (months === undefined) {
        months = mapMonths(dt => this.extractFromDate(dt, intl, 'month'))
        cache.set(formatStr, months)
      }

      return months
    }
  }

  /**
   *
   * @param {'long' | 'short' | 'narrow' | 'numeric'} style
   * @param {boolean} format
   * @return {Array<string>}
   */
  listWeekdays (style, format = false) {
    const mode = this.listingMode
    if (mode === 'error') {
      return null
    } else if (mode === 'en') {
      return getWeekdays(style)
    } else {
      const cache = ObjectCache.get(this)
      const intl = format
        ? { weekday: style, year: 'numeric', month: 'long', day: 'numeric' }
        : { weekday: style }

      const formatStr = format ? 'weekdaysCacheFormat' : 'weekdaysCacheStandalone'
      let weekdays = cache.get(formatStr)

      if (weekdays === undefined) {
        weekdays = mapWeekdays(dt => this.extractFromDate(dt, intl, 'weekday'))
        cache.set(formatStr, weekdays)
      }

      return weekdays
    }
  }

  /**
   * Extract a field out of a locale formatted date.
   * @param {ISODate | ISOTime | Date | number} date
   * @param {Intl.DateTimeFormatOptions} intlDateTimeOptions
   * @param {string} field
   * @returns {string}
   */
  extractFromDate (date, intlDateTimeOptions, field) {
    const cache = ObjectCache.get(this)
    const keyHash = hashObject(intlDateTimeOptions)
    let df = cache.get(keyHash)

    if (df === undefined) {
      df = new DateTimeFormat(this.toString(), intlDateTimeOptions)
      cache.set(keyHash, df)
    }

    const results = df.formatToParts(date)
    const matching = results.find(m => m.type.toLowerCase() === field)
    return matching ? matching.value : null
  }

  get listingMode () {
    const isActuallyEn = this.language === 'en'
    const numberingSystemID = SlotMap.getInternal(this, 'numberingSystemID')

    const hasNo = (
      (
        numberingSystemID === undefined ||
        numberingSystemID === 'latn'
      ) &&
      (
        this.outputCalendar === undefined ||
        this.outputCalendar === 'gregory'
      )
    )

    return isActuallyEn && hasNo ? 'en' : 'intl'
  }
}

/*
console.log(new Locale('en-US-u-tz-aiaxa'))
console.log(new Locale('en-US-u-tz-aiaxa').timeZone)
console.log(new Locale('es-US-u-tz-uschi'))
console.log(new Locale('en-US-u-tz-uschi').listMonths('long'))
console.log(Locale.systemLocale.listMonths('short'))
console.log(new Intl.DateTimeFormat('en-US-u-tz-aiaxa').resolvedOptions())
*/
